import '@atlaskit/css-reset';
import React, { useCallback, useMemo, useState } from 'react';
import { FlagsProvider } from '@atlaskit/flag/flags-provider';
import Suspense from '@atlaskit/react-ufo/suspense';
import UFOSegment from '@atlaskit/react-ufo/ufo-segment';
import HeartbeatDisplay from '@atlassian/heartbeat/heartbeat';
import { PreloadedQuery, RelayEnvironmentProvider } from 'react-relay';
import { AkAnalyticsListeners } from '@townsquare/analytics/ak-listener';
import { AnalyticsClientImpl } from '@townsquare/analytics/client';
import { AnalyticsContext } from '@townsquare/analytics/context';
import { useAtlasKitAnalytics } from '@townsquare/analytics/use-ak-analytics';
import { AppContext } from '@townsquare/app-context';
import { useLoadAppsFromResource } from '@townsquare/apps-store/hooks';
import { AuthCheck } from '@townsquare/auth';
import { StorageAuthCheck } from '@townsquare/auth/storage-auth-check';
import { returnAfterLogin } from '@townsquare/auth-utils';
import { useBrowserStorageControls } from '@townsquare/auth-utils/use-browser-storage-controls';
import { useCloudId } from '@townsquare/collab-context';
import { CrossFlowProvider } from '@townsquare/crossflow/provider';
import { CrossFlowWAC } from '@townsquare/crossflow/wac';
import { Drawers } from '@townsquare/drawers';
import { Flags } from '@townsquare/flags/wrapper';
import { HelpContextProvider } from '@townsquare/help-context';
import { UserLocaleOverrideHandler } from '@townsquare/i18n/override';
import TownsquareIntlProvider from '@townsquare/i18n/provider';
import { Modals } from '@townsquare/modals/ui';
import { RouteListener } from '@townsquare/performance/route-listener';
import { PostOfficeContextProvider } from '@townsquare/post-office/post-office-provider';
import { SharedPlacementMessagesProvider } from '@townsquare/post-office/shared-placement-messages-provider';
import { TownsquarePreviewPanelsProvider } from '@townsquare/preview-panels/townsquarePreviewPanelsProvider';
import { initialiseAggRelayEnvironment } from '@townsquare/relay-utils/agg-environment';
import { getRelayEnvironment } from '@townsquare/relay-utils/townsquare-environment';
import { RouteBlocker } from '@townsquare/route-blocker';
import { CapabilityRouter } from '@townsquare/router/capability-router';
import { CapabilityRouterFromVolt } from '@townsquare/router/capability-router-from-volt';
import { createBrowserHistory } from '@townsquare/router/createBrowserHistory';
import { isEmbedRoute, matchRoute } from '@townsquare/router/primitives/match-route';
import { useRouteListeners } from '@townsquare/router/use-route-listeners';
import { useQueryParam } from '@townsquare/router/useQueryParam';
import { RovoCreditsProvider } from '@townsquare/rovo/credits-provider';
import { RovoChatSubscribers } from '@townsquare/rovo-chat/subscribers';
import { createSearchParamsFromCurrentUrl } from '@townsquare/search-params';
import { FullScreenSpaLoader } from '@townsquare/spa-loader';
import { fg } from '@townsquare/stat-sig/gate';
import { GlobalTheming } from '@townsquare/theme/global-theming';
import { AnalyticAttributeHandler } from './AnalyticsAttributeHandler';
import { RovoAppificationHandler } from './RovoAppificationHandler';
import { Bootstrap, BootstrapResult } from './bootstrap/Bootstrap';
import { BootstrapFromVolt, BootstrapFromVoltResult } from './bootstrap/BootstrapFromVolt';
import { BootstrapQuery } from './bootstrap/__generated__/BootstrapQuery.graphql';
import { getPreloadedQueryRef } from './bootstrap/preload';
import { readBootstrapVoltPayload } from './bootstrap/voltPayload';
import { useKeyboardShortcuts } from './navigation/use-keyboard-shortcuts';
import { EmbedRoute } from './router/EmbedRoute';
import { GriffinRoute } from './router/GriffinRoute';
import { useHomeRoutes, useRoute } from './router/hooks';
import { routes } from './router/routes';
const analyticsClient = new AnalyticsClientImpl('web', 'townsquare');
const history = createBrowserHistory();
const EnforceCloudId = (props: {
  cloudId?: string;
}) => {
  const route = useRoute();
  const collabContextCloudId = useCloudId();
  const cloudId = props.cloudId ?? collabContextCloudId;
  const [qsCloudId, setQsCloudId] = useQueryParam('cloudId');
  if ('routeType' in route && route.routeType === 'global' && qsCloudId !== cloudId) {
    setQsCloudId(cloudId ?? undefined, 'replace');
  }
  return null;
};

/**
 * Discriminated union over the two bootstrap branches:
 * - `gql`: data came from the Relay-bound `Bootstrap` (preloaded query).
 *   `relayKey` is a `CapabilityRouterFragment$key` — pass to `<CapabilityRouter>`.
 * - `volt`: data came from the Volt JSON-inject `BootstrapFromVolt`.
 *   `workspaceVolt` is plain workspace data — pass to `<CapabilityRouterFromVolt>`
 *   once that variant exists for home; for now the GQL branch is the only
 *   path that hits `<CapabilityRouter>`.
 *
 * Every other field is identical between the two; the discriminant lets
 * `InsideBootstrap` mount the right CapabilityRouter without any other code
 * needing to care which branch fired.
 */
type InsideBootstrapData = ({
  source: 'gql';
} & BootstrapResult) | ({
  source: 'volt';
} & BootstrapFromVoltResult);
const InsideBootstrap = (data: InsideBootstrapData) => {
  // Compliance control
  useBrowserStorageControls();
  const homeRoutes = useHomeRoutes();
  const akAnalytics = useAtlasKitAnalytics();
  initialiseAggRelayEnvironment(data.collabContext?.cloudId ?? undefined);
  useKeyboardShortcuts();
  useLoadAppsFromResource();
  const [themeOverride] = useQueryParam('themeOverride');
  const {
    embed
  } = useRoute();
  const capabilityRouterChildren = <RovoCreditsProvider>
			<TownsquarePreviewPanelsProvider>
				<Suspense interactionName="home-route-switcher" fallback={<FullScreenSpaLoader />}>
					<RouteListener />
					<RouteSwitcher />
				</Suspense>
			</TownsquarePreviewPanelsProvider>
		</RovoCreditsProvider>;
  const capabilityRouter = data.source === 'gql' ? <CapabilityRouter history={history} resourceContext={data} data={data.relayKey} routes={homeRoutes}>
				{capabilityRouterChildren}
			</CapabilityRouter> : <CapabilityRouterFromVolt history={history} resourceContext={data} capabilities={data.workspaceVolt?.userCapabilities?.capabilities} routes={homeRoutes}>
				{capabilityRouterChildren}
			</CapabilityRouterFromVolt>;
  return <RelayEnvironmentProvider environment={getRelayEnvironment(data.workspace?.cloudId ?? undefined)}>
			<CrossFlowProvider>
				<AkAnalyticsListeners client={akAnalytics}>
					<EnforceCloudId cloudId={data.workspace?.cloudId} />
					<AnalyticAttributeHandler />
					<RovoAppificationHandler />
					<UserLocaleOverrideHandler />
					<RovoChatSubscribers />
					{/* Restrict theme overrides to embeds only */}
					<GlobalTheming colorModeOverride={embed ? themeOverride : undefined} />
					<HeartbeatDisplay onAuthenticationFailed={returnAfterLogin} />
					{capabilityRouter}
					<Modals />
					<Drawers />
					<Flags />
					<RouteBlocker />
				</AkAnalyticsListeners>
			</CrossFlowProvider>
		</RelayEnvironmentProvider>;
};
export function App(): React.JSX.Element {
  const urlSearch = Object.fromEntries(createSearchParamsFromCurrentUrl());
  const matchedRoute = matchRoute(routes, window.location.pathname, urlSearch);
  const AuthenticationCheck = isEmbedRoute(matchedRoute?.route) ? StorageAuthCheck : AuthCheck;

  // Bootstrap branch picker. Presence of the server-inlined payload on the
  // document IS the signal — the client has no gate of its own. Captured
  // once at mount; the global is set during document boot and is stable.
  const voltPayload = useMemo(() => readBootstrapVoltPayload(), []);
  const useVoltBranch = voltPayload != null;
  // https://switcheroo.atlassian.com/ui/gates/4990f8ae-71f4-4526-ade5-b16db7da4f60/key/townsquare_post-office-context-after-bootstrap
  const shouldUsePostOfficeContextAfterBootstrap = fg('townsquare_post-office-context-after-bootstrap');
  const [preloadedQueryRef, setPreloadedQueryRef] = useState<PreloadedQuery<BootstrapQuery> | null>(null);
  const onAuthDone = useCallback(() => {
    // Volt branch reads the inlined payload; no need for the Relay preload.
    if (useVoltBranch) {
      return;
    }
    setPreloadedQueryRef(getPreloadedQueryRef());
  }, [useVoltBranch]);
  const bootstrapContent = <HelpContextProvider>
			<FlagsProvider>
				{useVoltBranch ? <BootstrapFromVolt payload={voltPayload!} matchedRoute={matchedRoute} history={history} urlSearch={urlSearch}>
						{data => <InsideBootstrap source="volt" {...data} />}
					</BootstrapFromVolt> : preloadedQueryRef && <Bootstrap queryRef={preloadedQueryRef} history={history}>
							{data => <InsideBootstrap source="gql" {...data} />}
						</Bootstrap>}
			</FlagsProvider>
		</HelpContextProvider>;
  const postOfficeContent = shouldUsePostOfficeContextAfterBootstrap ? bootstrapContent : <PostOfficeContextProvider>
			<SharedPlacementMessagesProvider>{bootstrapContent}</SharedPlacementMessagesProvider>
		</PostOfficeContextProvider>;
  return <UFOSegment name="atlassian-home-root">
			<AuthenticationCheck onAuthDone={onAuthDone}>
				<AppContext.Provider value={{
        app: 'home'
      }}>
					<TownsquareIntlProvider app="home">
						<RelayEnvironmentProvider environment={getRelayEnvironment()}>
							<AnalyticsContext.Provider value={analyticsClient}>
								{postOfficeContent}
								{/* WAC via Start Cross Flow touch point support */}
								<CrossFlowWAC />
							</AnalyticsContext.Provider>
						</RelayEnvironmentProvider>
					</TownsquareIntlProvider>
				</AppContext.Provider>
			</AuthenticationCheck>
		</UFOSegment>;
}
function RouteSwitcher() {
  const {
    embed
  } = useRoute();
  useRouteListeners(); // Required to init analytics
  return embed ? <EmbedRoute /> : <GriffinRoute />;
}
export default App;